import axios from "axios";
import { extractError, getHttpStatus } from "../utils/apiErrorUtils";
import { UPLOAD_TIMEOUT_MS } from "../utils/axiosTimeouts";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

const rejectWithApiError = (error, fallback) => {
  const message = extractError(error, fallback);
  const enhanced = new Error(message);
  enhanced.status = getHttpStatus(error);
  return Promise.reject(enhanced);
};

/**
 * Stamp the device-clock instant a challenge payload was RECEIVED at (#260).
 *
 * The server publishes `serverNow` beside `expiresAt`, so their difference is a
 * skew-free LIFETIME. Turning that lifetime back into a deadline needs to know
 * when the countdown started, and the only honest answer is "when this response
 * landed" -- measured with the same clock that will later ask whether the
 * deadline has passed, so the device's offset appears on both sides and cancels.
 *
 * It has to be stamped at the RESPONSE BOUNDARY. Fix sprint 91 sampled the server
 * clock on the first render that saw the payload instead, and both skeptics
 * killed it on the same defect: that sample cannot tell CLOCK SKEW from PAYLOAD
 * AGE. The collection page loads the challenge list from an effect that does not
 * re-run while the page sits open (CollectionPage.jsx, deps `[user?._id,
 * isLoading, collectionCount]`) and hands a row to the modal only once the owner
 * opens one, so the sample landed N minutes after the fetch and read as N
 * minutes of skew -- silently granting every pending challenge N extra minutes
 * on a perfectly correct clock, and arming Submit on a challenge the server
 * 409s. At the response boundary the age is zero by construction.
 *
 * Additive: nothing the server sent is rewritten. A non-object body is passed
 * through untouched.
 */
const stampReceipt = (challenge) =>
  challenge && typeof challenge === "object"
    ? { ...challenge, receivedAtMs: Date.now() }
    : challenge;

export const createCardChallenge = async (userCardId) => {
  try {
    const { data } = await axios.post(`${apiUrl}/verification/card-challenges`, {
      userCardId,
    });
    return stampReceipt(data);
  } catch (error) {
    return rejectWithApiError(error, "Failed to create photo challenge");
  }
};

export const getMyCardChallenges = async ({ userCardId } = {}) => {
  try {
    const params = {};
    if (userCardId) params.userCardId = userCardId;
    const { data } = await axios.get(`${apiUrl}/verification/card-challenges/mine`, {
      params,
    });
    return (data?.challenges || []).map(stampReceipt);
  } catch (error) {
    return rejectWithApiError(error, "Failed to load photo challenge status");
  }
};

export const uploadChallengeEvidence = async (file) => {
  try {
    const formData = new FormData();
    formData.append("evidence", file);
    const { data } = await axios.post(`${apiUrl}/upload/evidence`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      // The third and last multipart post in the app, exempt from the global
      // default for the same reason as the two in collectionApi/userApi (#214).
      // A challenge photo is the evidence a badge rests on — a timed-out upload
      // here costs the user the whole verification attempt.
      timeout: UPLOAD_TIMEOUT_MS,
    });
    return data?.evidence?.[0];
  } catch (error) {
    return rejectWithApiError(error, "Failed to upload challenge evidence");
  }
};

export const submitChallengeEvidence = async (challengeId, storageKey) => {
  try {
    const { data } = await axios.post(
      `${apiUrl}/verification/card-challenges/${challengeId}/evidence`,
      { storageKey }
    );
    // Stamped like the other two, so every challenge payload that can reach the
    // modal carries the mark. `status` comes back as "submitted" here, and
    // `isExpiredChallenge` short-circuits on that before it reads either field.
    return stampReceipt(data);
  } catch (error) {
    return rejectWithApiError(error, "Failed to submit challenge evidence");
  }
};

export const adminGetCardChallenges = async ({ status, page, limit } = {}) => {
  try {
    const params = {};
    if (status) params.status = status;
    if (page !== undefined && page !== null) params.page = page;
    if (limit !== undefined && limit !== null) params.limit = limit;
    const { data } = await axios.get(`${apiUrl}/verification/admin/card-challenges`, {
      params,
    });
    return {
      challenges: data?.challenges ?? [],
      total: data?.total ?? 0,
      page: data?.page ?? 1,
      limit: data?.limit ?? 25,
    };
  } catch (error) {
    return rejectWithApiError(error, "Failed to load challenge evidence queue");
  }
};

export const adminGetChallengeEvidenceUrl = async (challengeId) => {
  try {
    const { data } = await axios.get(
      `${apiUrl}/verification/admin/card-challenges/${challengeId}/evidence-url`
    );
    return data;
  } catch (error) {
    return rejectWithApiError(error, "Failed to load evidence");
  }
};

export const adminReviewCardChallenge = async (challengeId, { decision, reviewNotes }) => {
  try {
    const body = { decision };
    const trimmed =
      reviewNotes !== undefined && reviewNotes !== null
        ? String(reviewNotes).trim()
        : "";
    if (trimmed) body.reviewNotes = trimmed;
    const { data } = await axios.put(
      `${apiUrl}/verification/admin/card-challenges/${challengeId}/review`,
      body
    );
    return data;
  } catch (error) {
    return rejectWithApiError(error, "Failed to save review");
  }
};
